import { useEffect, useRef, useState } from 'react';
import { useButton } from 'react-aria';
import {
  PeriodicityTypes,
  usePatchCustomerPeriodicities,
  useProductPeriodicityMap,
} from '@hungryroot/components-osn';
import { Frequency } from '@hungryroot/icons';
import { ButtonsGroup } from '@hungryroot/pho';
import { Heading } from '@hungryroot/ramen';
import { useIdentify } from '@hungryroot/rest-hooks';
import styles from './OSNSection.module.css';

export function OSNSection(props: { productId: number }) {
  const { productId } = props;

  return (
    <div className={styles['osn-section']}>
      <Heading className={styles.heading} variant="H6">
        How often should we send this?
      </Heading>
      <OSNButtonGroup productId={productId} />
    </div>
  );
}

type OSNButtonGroupProps = {
  productId: number;
  onChange?: ({ option }: { option: PeriodicityTypes | null }) => void;
  className?: string;
};

const periodicityOptions: { label: string; value: PeriodicityTypes }[] = [
  {
    label: 'Often',
    value: PeriodicityTypes.OFTEN,
  },
  {
    label: 'Sometimes',
    value: PeriodicityTypes.SOMETIMES,
  },
  {
    label: 'Never',
    value: PeriodicityTypes.NEVER,
  },
];

function OSNButtonGroup(props: OSNButtonGroupProps) {
  const { productId, onChange: passedOnChange } = props;

  const ref = useRef<HTMLButtonElement | null>(null);
  const [showButtonGroup, setShowButtonGroup] = useState(false);
  const [periodicityValue, setPeriodicityValue] =
    useState<PeriodicityTypes | null>(null);
  const { customerData } = useIdentify();

  const {
    data: productPeriodicityMapData,
    isLoading: productPeriodicityMapIsLoading,
  } = useProductPeriodicityMap();

  const patchCustomerPeriodicities = usePatchCustomerPeriodicities();

  useEffect(() => {
    if (!productPeriodicityMapIsLoading) {
      const periodicityValue = productPeriodicityMapData.get(productId) ?? null;
      const periodicity =
        periodicityValue === PeriodicityTypes.UNSET ? null : periodicityValue;

      setPeriodicityValue(periodicity);
      setShowButtonGroup(periodicity === null);
    }
  }, [productId, productPeriodicityMapData, productPeriodicityMapIsLoading]);

  const onChange = ({
    prevValue,
    newValue,
  }: {
    prevValue: PeriodicityTypes | null;
    newValue: PeriodicityTypes | null;
  }) => {
    if (newValue === null) {
      return;
    }

    setShowButtonGroup(false);

    if (customerData) {
      setPeriodicityValue(newValue);

      patchCustomerPeriodicities.mutate(
        {
          customerId: customerData.id,
          data: {
            periodicity: newValue,
            product: productId,
          },
        },
        {
          onError: () => {
            setPeriodicityValue(prevValue);
            passedOnChange?.({ option: prevValue });
          },
        }
      );
    }
  };

  const { buttonProps } = useButton(
    {
      onPress: () => setShowButtonGroup(true),
    },
    ref
  );

  if (showButtonGroup) {
    return (
      <ButtonsGroup
        className={styles['osn-button-group']}
        handleClick={(option) => {
          onChange({ prevValue: periodicityValue, newValue: option });
          passedOnChange?.({ option });
        }}
        selected={periodicityValue}
        options={periodicityOptions}
        labelVariant="H7"
      />
    );
  }

  const buttonLabel = periodicityOptions.find(
    (option) => option.value === periodicityValue
  )?.label;

  return (
    <button
      {...buttonProps}
      data-testid={`osn-single-button-${buttonLabel}`}
      ref={ref}
      className={styles['osn-single-button']}
    >
      <Frequency aria-hidden className={styles['osn-single-button-icon']} />
      <Heading variant="H7" className={styles['osn-single-button-text']}>
        {buttonLabel}
      </Heading>
    </button>
  );
}
